/**
 * steer.ts — Task C4 (+ 29.6-round rework) (+ Campaigns C1 Task 10: set_campaign)
 *
 * Seven steer tools that write URL search-params to change the live dashboard view.
 * Every change is reversible via the undo snapshot returned in the tool result's ui.
 *
 * Param keys and interlocks mirror the app's own state hooks exactly:
 *   - KPI cascade:  kpi_root / kpi_driver / kpi_value_type (useKpiCascadeState)
 *   - Timeframe:    tf_preset / from / to (useTimeframePresetState)
 *   - Filters:      dim.<name>=<values,> (useFilterDimsState + urlCodecs.encodeDimFilters)
 *   - Group-by:     groupby=<dims,> (useGroupByState)
 *   - Value types:  value_types=<vts,> (useValueTypeFilterState) — multi-select value_type filter
 *   - Columns:      columns=<dim> (useColumnsState) — a single pivot dim, must be ⊆ groupby
 *   - Campaign:     campaign=<id> (identity only) + tf_preset/from/to (useCampaignState /
 *                   campaignPredicate.ts) — every campaign-owned dim.* key (Topic Level 3 /
 *                   Topic Level 4 / Brand Casting Layer Level 1 / the exact Campaign dim) is
 *                   null-cleared on selection; per-fact filters (the exact Campaign dim on
 *                   facts that carry it, the attribution predicate elsewhere) are derived
 *                   downstream at walk-build time, never written to the URL (spec §4/§7/D-C3-1).
 *
 * Design decisions:
 * - setParams(null) deletes a key; mirrors useSetMultipleSearchParams behaviour.
 * - set_filters does REPLACE-ALL: clears every existing dim.* key, then sets the new
 *   ones. Undo covers the UNION of old+new dim.* keys so it is fully reversible.
 * - set_timeframe requires either a preset OR both from+to; neither → isError:true.
 * - set_timeframe CLAMPS a custom `from` up to the model floor (mirroring the walks.ts
 *   query-boundary clamp) and says so LOUDLY in the result — never silently diverges from
 *   what the charts render. Preset ranges already resolve against the floor upstream.
 * - set_value_types mirrors useValueTypeFilterState: comma-joined multi; empty → '' (= all).
 *   This is DISTINCT from the single-select kpi_value_type cascade axis (set_kpi's 3rd arg).
 * - set_columns mirrors useColumnsState: a SINGLE pivot dim that must be one of the current
 *   group-by dims; it validates that constraint and fails loudly (naming the offender) if not.
 * - set_campaign resolves against the Dataset Overview's campaign list: id exact-match first,
 *   then a case-insensitive label substring that must be unique — ambiguous or unknown input
 *   is an error listing the candidates, with NO write (mirrors set_timeframe's frame branch).
 *   Success writes buildCampaignSelectionParams(item) — identity + custom window, null-clearing
 *   EVERY campaign-owned dim.* key (ALL_CAMPAIGN_DIMS: the three managed attribution dims + the
 *   exact Campaign dim), clearing a prior campaign's leftovers of either form. Per-fact filters
 *   are never written to the URL — the fact-aware resolver derives them downstream (spec D-C3-1).
 *   {clear:true} removes ONLY the campaign identity (params stand), mirroring reconciliation.
 * - Nothing escapes run(): every throw is caught and returned as isError:true.
 */

import { encodeDimFilters } from '../../state/urlCodecs'
import { clampFromToFloor, getModelFloorIso } from '../../state/modelFloor'
import { buildCampaignSelectionParams } from '../../state/campaignPredicate'
import type { AdvisorTool, ToolResult, ToolUi } from '../runtime/types'
import type { DatasetOverview } from '../grounding/datasetOverview'
import type { RouteFamily } from '../../state/routeStateContract'

// ---------------------------------------------------------------------------
// Context shape injected by the advisor panel
// ---------------------------------------------------------------------------

export interface SteerCtx {
  setParams: (next: Record<string, string | null>) => void
  getParams: () => URLSearchParams
  /** Non-React accessor for the ≥model-floor cutoff (ISO yyyy-MM-dd, or null until the first
   *  get_controls resolves). Defaults to the live store accessor (getModelFloorIso, same source
   *  walks.ts uses); injectable so tests can pin a deterministic floor. */
  getModelFloor?: () => string | null
  /** consumed by set_timeframe frame steering (Task 6) */
  getOverview?: () => DatasetOverview | null
  /** FU-49: live route family at run() time (the app passes routeFamilyOf(location.pathname)).
   *  Steer tools write Review-owned URL keys; on any other family the write is wrong-layer
   *  (arrival hygiene deletes it) — the gate refuses instead, steering the model to
   *  propose_navigation. REQUIRED: every harness decides explicitly (no silent default). */
  getRouteFamily: () => RouteFamily | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DIM_PREFIX = 'dim.'

/** Snapshot the prior value of each key touched by `patch`.
 *  Returns { key: currentValue | null } for every key in the patch.
 *
 *  Undo/prune interplay (assessment, 29.6-round): master auto-prunes inapplicable dims on a
 *  fact/page change (ReviewPage pruneDimsToFact → useReviewPageState `setMany(..., {replace:true})`),
 *  rewriting groupby / dim.* / columns. That rewrite CANNOT corrupt an undo snapshot: the snapshot
 *  is an immutable capture of the pre-steer values, so applyUndo always restores exactly the
 *  pre-steer state regardless of any prune that ran in between. The only observable artifact is
 *  self-healing: if an undo restores a dim that is inapplicable on the now-current fact, the prune
 *  effect (reactive, keyed on the dropped-set) re-drops it and re-shows its notice — a transient
 *  flash, not a stuck/invalid state. set_kpi and set_timeframe are wholly unaffected (prune never
 *  touches the kpi cascade, tf_preset, from, to, or value_types). No fix warranted; see the
 *  WP-2A report for detail. */
function snapshotPrior(params: URLSearchParams, keys: string[]): Record<string, string | null> {
  const undo: Record<string, string | null> = {}
  for (const key of keys) {
    undo[key] = params.has(key) ? params.get(key)! : null
  }
  return undo
}

/** Collect all `dim.*` keys currently in URLSearchParams. */
function currentDimKeys(params: URLSearchParams): string[] {
  const keys: string[] = []
  for (const k of params.keys()) {
    if (k.startsWith(DIM_PREFIX)) keys.push(k)
  }
  return keys
}

/** Build a steer ui with the supplied label and undo map. */
function steerUi(label: string, undo: Record<string, string | null>): ToolUi {
  return { kind: 'steer', label, undo }
}

/** FU-49 gate copy — surfaced to the model as a tool error, so it must be actionable. */
export function steerRouteGateMessage(toolName: string): string {
  return (
    `${toolName}: steering is unavailable on this page — view steering writes Review page ` +
    'state, which only Review pages host (anywhere else the write would be discarded by URL ' +
    'hygiene). Use propose_navigation instead: propose the right Review page and carry the ' +
    'intended settings via presetView; the user applies them by clicking the proposal.'
  )
}

export const STEER_DESCRIPTION_SUFFIX =
  ' Only available on Review pages — from any other page, use propose_navigation (with presetView) instead.'

/** Wrap a steer tool with the FU-49 route gate: refuse (isError, no write, no undo chip)
 *  unless the CURRENT route family is review. Family is read at run() time, not build time —
 *  the panel survives navigation between turns. */
function withRouteGate(tool: AdvisorTool, getRouteFamily: () => RouteFamily | null): AdvisorTool {
  return {
    ...tool,
    description: tool.description + STEER_DESCRIPTION_SUFFIX,
    async run(input: unknown): Promise<ToolResult> {
      if (getRouteFamily() !== 'review') {
        return { content: steerRouteGateMessage(tool.name), isError: true }
      }
      return tool.run(input)
    },
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeSteerTools(ctx: SteerCtx): AdvisorTool[] {
  const { setParams, getParams } = ctx
  const getModelFloor = ctx.getModelFloor ?? getModelFloorIso
  const getOverview = ctx.getOverview ?? (() => null)

  // -------------------------------------------------------------------------
  // set_kpi
  // -------------------------------------------------------------------------

  const setKpi: AdvisorTool = {
    name: 'set_kpi',

    description: [
      'Change the active KPI on the current page (works on any analytics page, not just Campaign Performance).',
      'Provide root to switch the top-level KPI (clears driver + value_type).',
      'Provide root + driver to select a sub-axis (clears value_type).',
      'Provide root + driver + value_type to set all three cascade axes.',
      'Only supply the axes you want to change — omitted higher axes will be inferred.',
      'NOTE: value_type here is the single-select cascade axis (kpi_value_type); it is a',
      'different control from the multi-select value_type FILTER set by set_value_types.',
    ].join(' '),

    input_schema: {
      type: 'object',
      properties: {
        root: {
          type: 'string',
          description: 'Top-level KPI root, e.g. "nsp", "actual_sales", "sales_roi".',
        },
        driver: {
          type: 'string',
          description: 'KPI driver dimension (second cascade axis), e.g. "nsp", "total".',
        },
        value_type: {
          type: 'string',
          description: 'KPI value type (third cascade axis), e.g. "npv", "units".',
        },
      },
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        if (typeof input !== 'object' || input === null) {
          return { content: 'set_kpi: input must be a JSON object', isError: true }
        }
        const inp = input as Record<string, unknown>
        const root = typeof inp['root'] === 'string' ? inp['root'] : undefined
        const driver = typeof inp['driver'] === 'string' ? inp['driver'] : undefined
        const valueType = typeof inp['value_type'] === 'string' ? inp['value_type'] : undefined

        if (!root && !driver && !valueType) {
          return {
            content: 'set_kpi: provide at least one of root, driver, or value_type',
            isError: true,
          }
        }

        // Build patch according to cascade interlocks (mirrors useKpiCascadeState):
        //   setting root → clears driver + value_type
        //   setting driver (without root) → clears value_type
        //   setting value_type only → sets value_type alone
        const patch: Record<string, string | null> = {}
        const affectedKeys: string[] = []

        if (root !== undefined) {
          patch['kpi_root'] = root
          patch['kpi_driver'] = null
          patch['kpi_value_type'] = null
          affectedKeys.push('kpi_root', 'kpi_driver', 'kpi_value_type')
        }
        if (driver !== undefined) {
          patch['kpi_driver'] = driver
          // Clear value_type unless also provided in this call
          if (valueType === undefined) {
            patch['kpi_value_type'] = null
          }
          if (!affectedKeys.includes('kpi_driver')) affectedKeys.push('kpi_driver')
          if (!affectedKeys.includes('kpi_value_type')) affectedKeys.push('kpi_value_type')
        }
        if (valueType !== undefined) {
          patch['kpi_value_type'] = valueType
          if (!affectedKeys.includes('kpi_value_type')) affectedKeys.push('kpi_value_type')
        }

        const params = getParams()
        const undo = snapshotPrior(params, affectedKeys)

        setParams(patch)

        const parts: string[] = []
        if (root) parts.push(`root=${root}`)
        if (driver) parts.push(`driver=${driver}`)
        if (valueType) parts.push(`value_type=${valueType}`)
        const label = `Set KPI: ${parts.join(', ')}`

        return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  // -------------------------------------------------------------------------
  // set_timeframe
  // -------------------------------------------------------------------------

  const setTimeframe: AdvisorTool = {
    name: 'set_timeframe',

    description: [
      'Change the active timeframe on the dashboard.',
      'Either supply a named preset (last_4_weeks | last_12_weeks | last_quarter |',
      'ytd | fy_ytd | last_year | all_time), OR supply both from AND to as YYYY-MM-DD',
      '(sets tf_preset=custom automatically), OR supply frame with a named rolling frame id/label from the Dataset Overview.',
      'Supplying none of these is an error.',
      'The dashboard only reflects data at or after the model floor (the model-period start);',
      'a custom "from" earlier than the floor is clamped up to it (the charts do the same),',
      'and the result says so explicitly.',
    ].join(' '),

    input_schema: {
      type: 'object',
      properties: {
        preset: {
          type: 'string',
          enum: [
            'last_4_weeks',
            'last_12_weeks',
            'last_quarter',
            'ytd',
            'fy_ytd',
            'last_year',
            'all_time',
          ],
          description: 'Named timeframe preset.',
        },
        from: {
          type: 'string',
          description: 'Start date inclusive (YYYY-MM-DD). Requires "to" as well.',
        },
        to: {
          type: 'string',
          description: 'End date inclusive (YYYY-MM-DD). Requires "from" as well.',
        },
        frame: {
          type: 'string',
          description:
            'Named rolling frame — id or exact label from the Dataset Overview (e.g. "Last CY"). ' +
            "Sets the frame's current date window. Mutually exclusive with preset and from/to.",
        },
      },
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        if (typeof input !== 'object' || input === null) {
          return { content: 'set_timeframe: input must be a JSON object', isError: true }
        }
        const inp = input as Record<string, unknown>
        const preset = typeof inp['preset'] === 'string' ? inp['preset'] : undefined
        const from = typeof inp['from'] === 'string' ? inp['from'] : undefined
        const to = typeof inp['to'] === 'string' ? inp['to'] : undefined
        const frame = typeof inp['frame'] === 'string' ? inp['frame'] : undefined

        let patch: Record<string, string | null>
        let label: string
        // Extra note appended to the tool CONTENT (not the chip label) when a clamp happens,
        // so the model — and via the chip, the user — is told loudly, never silently.
        let clampNote = ''

        if (frame !== undefined) {
          if (preset !== undefined || from !== undefined || to !== undefined) {
            return {
              content:
                'set_timeframe: frame is mutually exclusive with preset and from/to — supply only one.',
              isError: true,
            }
          }
          const frames = getOverview()?.timeframe.namedFrames ?? null
          if (frames === null || frames.length === 0) {
            return {
              content:
                'set_timeframe: named frames are unavailable (dataset overview not loaded). ' +
                'Use a preset or explicit from/to instead.',
              isError: true,
            }
          }
          const needle = frame.trim().toLowerCase()
          const hit = frames.find((f) => f.id === frame.trim() || f.label.toLowerCase() === needle)
          if (!hit) {
            const listing = frames.map((f) => `${f.id} "${f.label}"`).join(', ')
            return {
              content: `set_timeframe: unknown frame "${frame}". Available named frames: ${listing}.`,
              isError: true,
            }
          }
          // Same clamp-and-say-so treatment as an explicit custom window.
          const floor = getModelFloor()
          const clampedFrom = clampFromToFloor(hit.from, floor)
          patch = { tf_preset: 'custom', from: clampedFrom, to: hit.to }
          label = `Set timeframe: ${hit.label} (${clampedFrom} → ${hit.to})`
          if (clampedFrom !== hit.from) {
            clampNote = ` (from clamped from ${hit.from} to ${clampedFrom} — the model floor)`
          }
          const params = getParams()
          const undo = snapshotPrior(params, Object.keys(patch))
          setParams(patch)
          return { content: `Applied: ${label}${clampNote}`, ui: steerUi(label, undo) }
        } else if (from && to) {
          // Mirror the walks.ts query-boundary clamp: a custom `from` before the model floor is
          // raised up to it (charts render floor-clamped data regardless). No-op when the floor
          // is null (not yet loaded) or `from` is already ≥ floor.
          const floor = getModelFloor()
          const clampedFrom = clampFromToFloor(from, floor)
          patch = { tf_preset: 'custom', from: clampedFrom, to }
          label = `Set timeframe: ${clampedFrom} → ${to}`
          if (clampedFrom !== from) {
            clampNote = ` (from clamped from ${from} to ${clampedFrom} — the model floor)`
          }
        } else if (preset) {
          patch = { tf_preset: preset, from: null, to: null }
          label = `Set timeframe: ${preset}`
        } else {
          return {
            content: 'set_timeframe: provide either a preset, or both from and to (YYYY-MM-DD).',
            isError: true,
          }
        }

        const params = getParams()
        const undo = snapshotPrior(params, Object.keys(patch))

        setParams(patch)

        return { content: `Applied: ${label}${clampNote}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  // -------------------------------------------------------------------------
  // set_filters
  // -------------------------------------------------------------------------

  const setFilters: AdvisorTool = {
    name: 'set_filters',

    description: [
      'Replace ALL active dimension filters (REPLACE-ALL, not additive).',
      'Keys are dimension names; values are arrays of allowed dim values.',
      'An empty object clears all filters. Omitting a dim previously filtered',
      'removes that filter.',
    ].join(' '),

    input_schema: {
      type: 'object',
      description:
        'Map of dimension name → allowed values. E.g. {"Medium Level 1": ["TV","Digital"]}.',
      additionalProperties: {
        type: 'array',
        items: { type: 'string' },
      },
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        if (typeof input !== 'object' || input === null) {
          return { content: 'set_filters: input must be a JSON object', isError: true }
        }
        const filters = input as Record<string, string[]>

        const params = getParams()

        // Existing dim.* keys
        const oldDimKeys = currentDimKeys(params)
        // New dim.* keys from the requested filters (via encodeDimFilters)
        const newEncoded = encodeDimFilters(filters)
        const newDimKeys = Object.keys(newEncoded)

        // Undo covers the UNION of old and new dim.* keys.
        // For each key in the union: prior value (or null if absent).
        const allDimKeys = Array.from(new Set([...oldDimKeys, ...newDimKeys]))
        const undo = snapshotPrior(params, allDimKeys)

        // Patch = null-out all old keys + set new keys
        const patch: Record<string, string | null> = {}
        for (const k of oldDimKeys) patch[k] = null
        for (const [k, v] of Object.entries(newEncoded)) patch[k] = v

        setParams(patch)

        const dimNames = Object.keys(filters)
        const label =
          dimNames.length === 0
            ? 'Clear all filters'
            : `Set filters: ${dimNames.map((n) => `${n}=[${filters[n]!.join(',')}]`).join('; ')}`

        return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  // -------------------------------------------------------------------------
  // set_group_by
  // -------------------------------------------------------------------------

  const setGroupBy: AdvisorTool = {
    name: 'set_group_by',

    description: [
      'Change the active group-by dimensions on the dashboard.',
      'Supply {"dims": [...]} with an array of dimension names. An empty array clears all group-by.',
      'Example: {"dims": ["Campaign", "Medium Level 1"]}.',
    ].join(' '),

    // Anthropic tool input_schema MUST be type 'object' at the top level —
    // a bare array schema is rejected upstream with invalid_request_error
    // (found live in the 2026-07-03 F2 gate; unit tests mock the API and
    // cannot catch it, buildTools.test.ts now enforces this invariant).
    input_schema: {
      type: 'object',
      properties: {
        dims: {
          type: 'array',
          items: { type: 'string' },
          description: 'Dimension names to group by. Empty array removes all group-by.',
        },
      },
      required: ['dims'],
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        const raw =
          typeof input === 'object' && input !== null
            ? (input as Record<string, unknown>)['dims']
            : undefined
        if (!Array.isArray(raw)) {
          return {
            content: 'set_group_by: input must be {"dims": [...]} with an array of strings',
            isError: true,
          }
        }
        const dims = raw as string[]
        for (const d of dims) {
          if (typeof d !== 'string') {
            return {
              content: 'set_group_by: each element must be a string',
              isError: true,
            }
          }
        }

        const params = getParams()
        const undo = snapshotPrior(params, ['groupby'])

        const patch: Record<string, string | null> = { groupby: dims.join(',') }
        setParams(patch)

        const label = dims.length === 0 ? 'Clear group-by' : `Set group-by: ${dims.join(', ')}`

        return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  // -------------------------------------------------------------------------
  // set_value_types (M2)
  // -------------------------------------------------------------------------

  const setValueTypes: AdvisorTool = {
    name: 'set_value_types',

    description: [
      'Set the multi-select Value Type filter (URL param value_types) — the control labelled',
      '"Value Type" on the page. Supply an array of value_type names to co-plot as separate',
      'series (e.g. ["grossadds","netsales","topups"] for actual_sales). An empty array (or no',
      'argument) clears the filter, which shows ALL value types.',
      'This filter ONLY has an effect for KPIs whose metadata carries more than one value_type',
      '(e.g. actual_sales); for single-value-type KPIs it is inert (the "Value Type" control is',
      "not even shown). It is a DIFFERENT mechanism from set_kpi's value_type argument, which is",
      'the single-select third cascade axis (kpi_value_type) — do not confuse the two.',
    ].join(' '),

    // Top-level 'object' required by the Anthropic tools API (see set_group_by).
    input_schema: {
      type: 'object',
      properties: {
        value_types: {
          type: 'array',
          items: { type: 'string' },
          description:
            'value_type names to show (co-plotted as separate series). Empty array (or omitted) clears the filter (= show all value types).',
        },
      },
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        // Omitted value_types (or {}) is treated as "clear" per the filter's semantics.
        const wrapped =
          typeof input === 'object' && input !== null
            ? (input as Record<string, unknown>)['value_types']
            : input
        const raw = wrapped ?? []
        if (!Array.isArray(raw)) {
          return {
            content:
              'set_value_types: input must be {"value_types": [...]} with an array of strings (or omitted to clear)',
            isError: true,
          }
        }
        for (const v of raw) {
          if (typeof v !== 'string') {
            return { content: 'set_value_types: each element must be a string', isError: true }
          }
        }
        const vts = raw as string[]

        const params = getParams()
        const undo = snapshotPrior(params, ['value_types'])

        // Mirror useValueTypeFilterState exactly: setter writes next.join(',') → '' when empty.
        setParams({ value_types: vts.join(',') })

        const label =
          vts.length === 0 ? 'Clear value-type filter' : `Set value types: ${vts.join(', ')}`

        return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  // -------------------------------------------------------------------------
  // set_columns (m1)
  // -------------------------------------------------------------------------

  const setColumns: AdvisorTool = {
    name: 'set_columns',

    description: [
      'Set the Columns pivot (URL param columns) — the single group-by dimension whose values',
      'become the table columns. It is single-valued: supply exactly one dimension name, which',
      'MUST be one of the dimensions currently in the group-by (columns ⊆ group-by). An empty',
      'string (or no column) clears the pivot. If the requested dimension is not in the current',
      'group-by this fails loudly (it does not silently add it) — add it with set_group_by first.',
    ].join(' '),

    input_schema: {
      type: 'object',
      properties: {
        column: {
          type: 'string',
          description:
            'A single dimension to pivot into table columns. Must be one of the current group-by dimensions. Empty string clears the pivot.',
        },
      },
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        // Accept an object (or omitted → clear); reject arrays/other scalars loudly.
        if (input != null && (typeof input !== 'object' || Array.isArray(input))) {
          return { content: 'set_columns: input must be a JSON object', isError: true }
        }
        const inp = (input ?? {}) as Record<string, unknown>
        const column = typeof inp['column'] === 'string' ? inp['column'] : ''

        const params = getParams()
        const undo = snapshotPrior(params, ['columns'])

        // Empty → clear the pivot (mirror useColumnsState: delete the key on empty/invalid).
        if (column === '') {
          setParams({ columns: null })
          const label = 'Clear columns pivot'
          return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
        }

        // Validate the columns ⊆ group-by invariant against the CURRENT groupby URL param
        // (decoded exactly like useGroupByState: comma-split, drop empties).
        const groupbyRaw = params.get('groupby')
        const groupByDims = groupbyRaw ? groupbyRaw.split(',').filter(Boolean) : []
        if (!groupByDims.includes(column)) {
          const current = groupByDims.length > 0 ? groupByDims.join(', ') : '(none)'
          return {
            content:
              `set_columns: "${column}" is not in the current group-by [${current}]. ` +
              'The columns pivot must be one of the active group-by dimensions — ' +
              'add it with set_group_by first, then set columns.',
            isError: true,
          }
        }

        setParams({ columns: column })
        const label = `Set columns pivot: ${column}`
        return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  // -------------------------------------------------------------------------
  // set_campaign (Campaigns C1, Task 10)
  // -------------------------------------------------------------------------

  const setCampaign: AdvisorTool = {
    name: 'set_campaign',

    description: [
      "Select a saved campaign from the Dataset Overview's campaign list — a saved time",
      'window plus campaign identity. Provide campaign as an id (exact match) or a label',
      '(case-insensitive substring, must match exactly one campaign). This is the SAME',
      'atomic write as picking the campaign in the Timeframe control: sets the custom',
      "window to the campaign's dates and the campaign identity (campaign=<id>), clearing",
      'every campaign-owned dim.* param from the URL (Topic Level 3 / Topic Level 4 /',
      'Brand Casting Layer Level 1 / the exact Campaign dim). Per-fact filters are derived',
      'downstream at query time, never written to the URL: on facts that carry the exact',
      'Campaign dim selection filters exactly by campaign id; elsewhere it falls back to the',
      'attribution predicate automatically, degrading further to window-only (with a visible',
      'notice) on a fact that carries neither. A ratio metric resolves exact per campaign only',
      'where BOTH of its component facts carry the Campaign dim; otherwise it reads as',
      'attribution on both sides — never a one-sided mix — and the visible notice says so.',
      'Pass {"clear": true} instead to remove ONLY the campaign identity, leaving the current',
      'window and filters as they are.',
    ].join(' '),

    input_schema: {
      type: 'object',
      properties: {
        campaign: {
          type: 'string',
          description: 'Campaign id (exact) or label (case-insensitive substring, must be unique).',
        },
        clear: {
          type: 'boolean',
          description: 'Set true to clear the campaign identity only, without changing params.',
        },
      },
    },

    async run(input: unknown): Promise<ToolResult> {
      try {
        if (typeof input !== 'object' || input === null) {
          return { content: 'set_campaign: input must be a JSON object', isError: true }
        }
        const inp = input as Record<string, unknown>
        const clear = inp['clear'] === true
        const campaign = typeof inp['campaign'] === 'string' ? inp['campaign'] : undefined

        if (clear) {
          if (campaign !== undefined) {
            return {
              content: 'set_campaign: clear is exclusive with campaign — supply only one.',
              isError: true,
            }
          }
          const params = getParams()
          const undo = snapshotPrior(params, ['campaign'])
          setParams({ campaign: null })
          const label = 'Clear campaign'
          return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
        }

        if (!campaign) {
          return {
            content: 'set_campaign: provide campaign (id or label), or {"clear": true}.',
            isError: true,
          }
        }

        const campaigns = getOverview()?.timeframe.campaigns ?? null
        if (campaigns === null || campaigns.length === 0) {
          return {
            content: 'set_campaign: campaigns are unavailable (dataset overview not loaded).',
            isError: true,
          }
        }

        // Resolve: exact id first, then a unique case-insensitive label substring.
        let hit = campaigns.find((c) => c.id === campaign)
        if (!hit) {
          const needle = campaign.trim().toLowerCase()
          const matches = campaigns.filter((c) => c.label.toLowerCase().includes(needle))
          if (matches.length === 1) {
            hit = matches[0]
          } else if (matches.length > 1) {
            const listing = matches.map((c) => `${c.id} "${c.label}"`).join(', ')
            return {
              content: `set_campaign: "${campaign}" matches multiple campaigns: ${listing}. Use the id, or a more specific label.`,
              isError: true,
            }
          }
        }
        if (!hit) {
          const listing = campaigns.map((c) => `${c.id} "${c.label}"`).join(', ')
          return {
            content: `set_campaign: unknown campaign "${campaign}". Available campaigns: ${listing}.`,
            isError: true,
          }
        }

        const patch = buildCampaignSelectionParams(hit.item)
        const params = getParams()
        const undo = snapshotPrior(params, Object.keys(patch))

        setParams(patch)

        const label = `Set campaign: ${hit.label}`
        return { content: `Applied: ${label}`, ui: steerUi(label, undo) }
      } catch (err) {
        return { content: err instanceof Error ? err.message : String(err), isError: true }
      }
    },
  }

  return [setKpi, setTimeframe, setFilters, setGroupBy, setValueTypes, setColumns, setCampaign].map(
    (t) => withRouteGate(t, ctx.getRouteFamily),
  )
}
